/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { buildHashWithAuthMode, getLocationHash, serializeQueryOmittingAuthMode } from 'js/lib/authModeDeepLinkUtils';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Button, Grid } from '@coursera/cds-core';

import AuthenticationV3CtaTracking from 'bundles/authentication-common/components/AuthenticationV3CtaTracking';
import type { AuthButtonProps } from 'bundles/composable-page-header/types';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

const styles = {
  secondaryAction: css`
    && {
      display: block;
      background: none;
      font-weight: normal;
      max-height: 56px;
      max-width: 120px;
      white-space: nowrap;
      padding: var(--cds-spacing-150);

      :hover {
        color: var(--cds-color-interactive-primary);
      }

      :focus {
        outline: 5px auto -webkit-focus-ring-color;
        outline-offset: -2px;
        box-shadow: none !important;
      }
    }
  `,
  primaryAction: css`
    && {
      font-weight: 700;
      max-height: 56px;
      max-width: 120px;
      padding: var(--cds-spacing-100) var(--cds-spacing-150);
      text-decoration: none !important;
    }
  `,
};

type UnauthenticatedActionsProps = {
  buttons: AuthButtonProps[];
};

const UnauthenticatedActionsGridItem = ({ buttons }: UnauthenticatedActionsProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const getAuthHref = (authMode: 'signup' | 'login') => {
    const search = serializeQueryOmittingAuthMode(location.query);
    const hash = buildHashWithAuthMode(getLocationHash(location), authMode);
    return `${location.pathname}${search ? `?${search}` : ''}${hash}`;
  };

  const handleAuthClick = (authMode: 'signup' | 'login') => {
    const search = serializeQueryOmittingAuthMode(location.query);
    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
      hash: buildHashWithAuthMode(getLocationHash(location), authMode),
    });
  };

  return (
    <AuthenticationV3CtaTracking>
      {(trackV3) => (
        <React.Fragment>
          {buttons.map(({ label, authMode }) => (
            <Grid item key={`${authMode}_button_desktop`} wrap="nowrap">
              <Button
                css={authMode === 'signup' || buttons.length === 1 ? styles.primaryAction : styles.secondaryAction}
                href={getAuthHref(authMode)}
                variant={authMode === 'signup' || buttons.length === 1 ? 'secondary' : 'ghost'}
                size="small"
                component={TrackedLink2}
                trackingName="header_right_nav_button"
                textAlign="center"
                data-e2e={`header-${authMode}-button`}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  trackV3({ name: authMode, sectionName: 'page_header' });
                  handleAuthClick(authMode);
                }}
              >
                {label}
              </Button>
            </Grid>
          ))}
        </React.Fragment>
      )}
    </AuthenticationV3CtaTracking>
  );
};

export default UnauthenticatedActionsGridItem;
